/**
 * Enums du domaine — déclarées UNE SEULE FOIS ici (ARCHITECTURE §3, §6).
 *
 * Elles sont réutilisées telles quelles par :
 *   - Drizzle (`pgEnum('phase_saison', PHASE_SAISON)`) côté backend ;
 *   - Zod (`z.enum(PHASE_SAISON)`) côté DTO ;
 *   - le frontend, pour les libellés et les filtres.
 *
 * Convention : valeurs en minuscules françaises, sans accent ni espace
 * (`'non_paye'`, `'cloturee'`). Le libellé humain accentué vit dans
 * `LIBELLES_*` en bas de fichier — NF-06 : aucune chaîne d'IHM en dur ailleurs.
 */
/* -------------------------------------------------------------------------- */
/* Catalogue                                                                  */
/* -------------------------------------------------------------------------- */
/** Cycle de vie d'une saison (SPEC §6, CAT-01). Transitions manuelles. */
export const PHASE_SAISON = ['preparation', 'interne', 'externe', 'cloturee'];
/** Une représentation est programmée, ou annulée (CAT-04). Jamais l'inverse. */
export const STATUT_REPRESENTATION = ['programmee', 'annulee'];
/* -------------------------------------------------------------------------- */
/* Lieu — numéroté ou à placement libre (SPEC-BOUTIQUE §8 ter, LIB-01)        */
/* -------------------------------------------------------------------------- */
/**
 * ★★ LIB-01 — LA décision de conception du chantier Q, et elle tient en une
 * phrase : **le siège devient une unité comptable, pas une place physique.**
 *
 * Le réflexe aurait été de rendre `billet.siege_id` facultatif pour vendre des
 * entrées sans place assignée. Ce serait le pire choix possible : trois
 * invariants garantis par PostgreSQL en dépendent — I-01 (un siège, un billet
 * valide, une représentation), I-08 (une occupation active par siège) et I-09
 * (le siège appartient à la salle de la représentation). Les rendre facultatifs
 * remplacerait la mécanique la mieux éprouvée du produit par un compteur qu'il
 * faudrait protéger à la main contre la concurrence — précisément le problème
 * qu'`occupation_siege` a résolu.
 *
 *  - `numerote` : le comportement historique, et le DÉFAUT. Un plan, des rangs,
 *    des numéros, un fauteuil qu'on choisit ;
 *  - `libre`    : une jauge. Le lieu engendre autant de sièges que sa jauge,
 *    **sans rang ni numéro signifiants** (`RANG_PLACEMENT_LIBRE`). L'acheteur
 *    n'en choisit aucun : il choisit une QUANTITÉ, et le système lui en
 *    attribue en silence. Le billet porte « entrée », pas « rang F, siège 12 ».
 *
 * Conséquence — et c'est ce qui rend ce chantier bon marché : la boutique, la
 * concurrence prouvée à quarante acheteurs simultanés, le contrôle d'accès et
 * les neuf invariants continuent de fonctionner **sans aucune modification**.
 */
export const MODE_SALLE = ['numerote', 'libre'];
/**
 * ★ LIB-06 — le garde-fou de volumétrie, en un nombre.
 *
 * NF-01 dimensionne le `SeatMap` pour 500 sièges. Un lieu à placement libre
 * peut légitimement dépasser (une salle de concert debout, un open-air), et
 * c'est exactement pour cela que LIB-03 masque le plan partout : ce n'est pas
 * une préférence d'affichage, c'est ce qui empêche d'envoyer 5 000 fauteuils à
 * un navigateur. La borne ci-dessous reste, elle, une borne dure — au-delà,
 * SPEC-BOUTIQUE §8 ter D dit « sur devis », c'est-à-dire pas en libre-service.
 */
export const JAUGE_LIBRE_MIN = 1;
export const JAUGE_LIBRE_MAX = 5000;
export const LIBELLES_MODE_SALLE = {
    numerote: 'Salle numérotée (plan de salle)',
    libre: 'Lieu à placement libre (jauge)',
};
/* -------------------------------------------------------------------------- */
/* Tarification                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Ce sur quoi porte une ligne de tarif : un spectacle à l'unité, une formule,
 * ou — ★ T-09 (TAR-03) — la SAISON entière : la matrice « défaut saison »,
 * repli d'un spectacle qui n'a pas la sienne propre. `porteeId` vaut alors
 * `saison.id` (référence polymorphe non contrainte, comme pour les deux
 * autres portées — cf. `tarif.porteeId`, `db/schema/tarification.ts`).
 */
export const PORTEE_TARIF = ['spectacle', 'formule', 'saison'];
/* -------------------------------------------------------------------------- */
/* Formules                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Nature d'une formule d'abonnement (SPEC-FONCTIONNELLE §5) :
 *  - `serie`  : programme imposé, sièges figés pour la saison, billets générés
 *               en bloc à la confirmation (SOU-02) ;
 *  - `choix`  : N spectacles distincts au choix, un billet par consommation (I-04) ;
 *  - `carnet` : N crédits librement dépensables, plusieurs entrées possibles
 *               le même soir (I-03).
 */
export const NATURE_FORMULE = ['serie', 'choix', 'carnet'];
/* -------------------------------------------------------------------------- */
/* Souscriptions, ventes, argent                                              */
/* -------------------------------------------------------------------------- */
/** Cycle de vie d'une souscription (SPEC §6). */
export const STATUT_SOUSCRIPTION = ['brouillon', 'confirmee', 'annulee'];
/**
 * Statut de paiement binaire d'une souscription ou d'une vente (PAY-01).
 * Aucune facturation, aucune relance : c'est une information, pas un processus.
 */
export const STATUT_PAIEMENT = ['non_paye', 'paye'];
/* -------------------------------------------------------------------------- */
/* Billetterie                                                                */
/* -------------------------------------------------------------------------- */
/**
 * D'où vient le droit d'entrée (I-02) :
 *  - `abonnement` : rattaché à une souscription ;
 *  - `unite`      : rattaché à une vente guichet ;
 *  - `invitation` : montant obligatoirement 0, motif obligatoire (VEN-03).
 */
export const ORIGINE_BILLET = ['abonnement', 'unite', 'invitation'];
/** Un billet est valide, ou annulé (avec motif, auteur, date). Jamais l'inverse. */
export const STATUT_BILLET = ['valide', 'annule'];
/** Une carte d'abonné est active, ou révoquée (DOC-04, régénération). */
export const STATUT_CARTE = ['active', 'revoquee'];
/* -------------------------------------------------------------------------- */
/* Occupation d'un siège — I-08 (SPEC-BOUTIQUE §2)                            */
/* -------------------------------------------------------------------------- */
/**
 * ★ I-08 — POURQUOI CETTE ENUM EXISTE.
 *
 * Jusqu'ici, « ce siège est-il pris ? » se répondait en interrogeant plusieurs
 * tables (`billet`, `souscription_siege`, `proposition_siege`). Tant que seuls
 * trois guichetiers écrivaient, la fenêtre de course entre deux lectures était
 * théorique. La boutique publique la rend réelle : deux visiteurs peuvent viser
 * le même fauteuil à la même seconde, et PostgreSQL ne sait pas contraindre
 * deux tables ensemble.
 *
 * `occupation_siege` est le point d'arbitrage UNIQUE. Sa `nature` dit ce qui
 * retient le siège :
 *
 *  - `panier`       : retenu temporairement pendant un achat en ligne. C'est la
 *                     SEULE nature qui porte une date d'expiration, et la seule
 *                     qu'une autre transaction puisse voler une fois périmée ;
 *  - `souscription` : promis à un abonné (SOU-01). Sa souscription série a figé
 *                     la place, les billets ne sont pas encore émis ;
 *  - `billet`       : vendu, donné ou abonné — un billet valide existe.
 *
 * La progression normale d'une place d'abonné est `souscription → billet` ; la
 * progression d'un achat en ligne sera `panier → billet`.
 */
export const NATURE_OCCUPATION = ['panier', 'souscription', 'billet', 'contingent'];
/* -------------------------------------------------------------------------- */
/* Boutique en ligne — panier et rétention (SPEC-BOUTIQUE §2.4, §3.1)          */
/* -------------------------------------------------------------------------- */
/**
 * Cycle de vie d'un panier de la boutique.
 *
 *  - `actif`     : le visiteur compose sa commande, ses places sont retenues
 *                  jusqu'à `expire_le` (REZ-01) ;
 *  - `expire`    : le compte à rebours est écoulé. ★ **Statut posé, pas
 *                  déduit** : c'est ce qui permet de dire au visiteur « votre
 *                  panier a expiré » au lieu de « panier introuvable », ce qui
 *                  est la différence entre une explication et une disparition ;
 *  - `abandonne` : le visiteur a vidé son panier lui-même ;
 *  - `valide`    : la commande est passée (lot paiement). État terminal.
 *
 * ⚠️ La JUSTESSE ne dépend jamais de ce statut : un siège se libère parce que
 * `occupation_siege.expire_le` est dépassé, pas parce qu'une ligne de `panier`
 * a été repeinte. Le statut sert à parler au visiteur, pas à arbitrer.
 */
export const STATUT_PANIER = ['actif', 'expire', 'abandonne', 'valide'];
/**
 * Ce qu'une ligne de panier achète (BOU-04, BOU-05) :
 *  - `place`      : un fauteuil pour UNE représentation, avec sa catégorie de
 *                   public — l'unité du panier multi-représentations ;
 *  - `abonnement` : une souscription à une formule. Pour une formule `serie`
 *                   elle porte un siège, tenu sur TOUTES les dates de la série ;
 *                   pour un carnet ou un « N au choix », elle n'en porte aucun.
 */
export const GENRE_LIGNE_PANIER = ['place', 'abonnement'];
/**
 * ★ BOU-11…BOU-13 — qui compose le panier, et sous quel régime de rétention.
 *
 *  - `public`  : un visiteur de la boutique. Cookie opaque, pas de session,
 *                rétention courte (REZ-01, 5 à 30 min) — c'est le régime
 *                d'origine de `panier`, celui que ce fichier décrivait déjà ;
 *  - `guichet` : un membre du personnel qui déroule le MÊME parcours pour le
 *                compte d'un tiers (« un acheteur au téléphone », BOU-11).
 *                Le panier est tracé (opérateur, horodatage — BOU-12) et **ne
 *                subit pas** le compte à rebours du public, mais il n'est pas
 *                pour autant sans expiration : une rétention plus longue
 *                l'arme quand même, pour ne jamais bloquer une place sans fin.
 *
 * La distinction ne change ni le modèle de rétention (`occupation_siege`,
 * I-08) ni la table `panier` elle-même : seuls diffèrent qui la possède
 * (`operateur_id` au lieu d'un cookie) et combien de temps elle tient.
 */
export const MODE_PANIER = ['public', 'guichet'];
/* -------------------------------------------------------------------------- */
/* Paiement en ligne — PAI-01…PAI-05 (SPEC-BOUTIQUE §4)                       */
/* -------------------------------------------------------------------------- */
/**
 * ★ PAI-02 — les états d'un **vrai** paiement, posés dès maintenant alors
 * qu'aucun prestataire n'encaisse encore.
 *
 * C'est une décision de modèle, pas d'affichage. Un domaine qui ne connaîtrait
 * que « payé / pas payé » (`STATUT_PAIEMENT`, PAY-01 — le pointage manuel du
 * guichet, qui reste) obligerait, le jour de l'intégration réelle, à
 * réécrire les chemins d'écriture pour introduire l'attente, l'échec et le
 * remboursement. On les écrit une fois, maintenant, pendant que le domaine est
 * jeune :
 *
 *  - `initie`     : l'intention existe, le payeur n'a rien fait encore ;
 *  - `en_attente` : le prestataire a la main (redirection 3-D Secure, e-banking,
 *                   virement annoncé). C'est l'état qui dure, et celui qu'un
 *                   modèle binaire ne sait pas dire ;
 *  - `confirme`   : l'argent est acquis. **Seul** état qui produit des billets ;
 *  - `echoue`     : refus du prestataire ou de la banque. Terminal ;
 *  - `abandonne`  : le payeur est parti. Terminal — distinct de `echoue`, parce
 *                   qu'un abandon massif se corrige (le parcours), pas un refus ;
 *  - `rembourse`  : geste humain après coup (SPEC-BOUTIQUE §9 : aucun
 *                   remboursement automatisé), tracé ici pour que la
 *                   comptabilité ne se lise pas dans les courriels.
 */
export const STATUT_TRANSACTION = [
    'initie',
    'en_attente',
    'confirme',
    'echoue',
    'abandonne',
    'rembourse',
];
/**
 * Transitions autorisées — la table de vérité du cycle de vie, en un endroit.
 *
 * Un rejeu vers l'état **courant** n'est pas une transition : c'est une
 * idempotence, traitée à part (PAI-03). Les trois états terminaux ne mènent
 * nulle part, sauf `confirme` → `rembourse`.
 */
export const TRANSITIONS_TRANSACTION = {
    initie: ['en_attente', 'confirme', 'echoue', 'abandonne'],
    en_attente: ['confirme', 'echoue', 'abandonne'],
    confirme: ['rembourse'],
    echoue: [],
    abandonne: [],
    rembourse: [],
};
/** `true` si le paiement peut passer de `depuis` à `vers`. */
export function transitionTransactionAutorisee(depuis, vers) {
    return TRANSITIONS_TRANSACTION[depuis].includes(vers);
}
/** Les états dont plus rien ne sort : le paiement a fini sa vie. */
export const STATUTS_TRANSACTION_TERMINAUX = [
    'echoue',
    'abandonne',
    'rembourse',
];
/**
 * Prestataires de paiement connus du domaine.
 *
 * Il n'y en a qu'un, et il n'encaisse rien (PAI-01, SPEC-BOUTIQUE §9). L'enum
 * existe quand même : c'est elle qui rend l'ajout de `datatrans` ou `postfinance`
 * une ligne de plus plutôt qu'une colonne de plus.
 */
export const PRESTATAIRE_PAIEMENT = ['simule', 'stripe', 'payrexx'];
/* -------------------------------------------------------------------------- */
/* Facturation QR — FAC-01…FAC-06 (SPEC-BOUTIQUE §5)                          */
/* -------------------------------------------------------------------------- */
/**
 * Type de référence structurée d'une facture (FAC-02). Le choix **découle** de
 * l'IBAN du théâtre, il ne se saisit pas — cf. `typeReferencePourIban()` dans
 * `qr-facture.ts`, qui porte la règle et la teste.
 */
export const TYPE_REFERENCE_FACTURE = ['qrr', 'scor', 'sans'];
/**
 * Une facture est émise, ou annulée. Jamais supprimée : un document numéroté
 * qui disparaît est un trou dans la comptabilité (ARCHITECTURE §3).
 */
export const STATUT_FACTURE = ['emise', 'annulee'];
/* -------------------------------------------------------------------------- */
/* Relances d'impayés — RLC-01…RLC-06 (SPEC-BOUTIQUE §8 bis B)                 */
/* -------------------------------------------------------------------------- */
/**
 * ★ RLC-04 — le catalogue FERMÉ des variables de substitution admises dans le
 * texte d'une relance. Fermé au sens strict : le service de validation
 * (`backend/modules/paiements/relance.ts`) refuse l'enregistrement d'un texte
 * qui contient une `{{variable}}` absente d'ici — jamais un courriel envoyé
 * avec un trou. Modifier cette liste change donc, le même jour, ce que
 * l'application accepte ET ce qu'elle documente.
 */
export const VARIABLES_RELANCE = [
    'nom',
    'montant',
    'numero_facture',
    'reference',
    'echeance',
    'lien_facture',
];
/** Libellé affiché dans la légende de l'écran de configuration (RLC-03). */
export const LIBELLES_VARIABLE_RELANCE = {
    nom: 'Nom du payeur',
    montant: 'Montant dû, formaté (ex. « CHF 120.00 »)',
    numero_facture: 'Numéro de la facture (ex. « 42 ») — court, à mettre dans l’objet',
    reference: 'Référence structurée de la facture QR, 27 chiffres — pour le corps, jamais pour l’objet',
    echeance: 'Date d’échéance (date de la facture + 30 jours)',
    lien_facture: 'Lien de téléchargement de la facture',
};
/* -------------------------------------------------------------------------- */
/* Campagne de réabonnement                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Cycle de vie d'une proposition de reconduction (CAM-05).
 * `expiree` est posé en bloc à l'ouverture de la phase externe (CAM-02).
 */
export const STATUT_PROPOSITION = ['en_attente', 'acceptee', 'declinee', 'expiree'];
/* -------------------------------------------------------------------------- */
/* Contrôle d'accès                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Résultat d'un passage au contrôle (SCAN-02, SCAN-05) :
 *  - `admis`   : premier passage d'un billet valide ;
 *  - `doublon` : billet déjà scanné (l'heure du premier passage est affichée) ;
 *  - `refuse`  : billet annulé, autre représentation, token inconnu, carte révoquée ;
 *  - `manuel`  : admission par recherche au nom, QR illisible ou absent.
 */
export const RESULTAT_SCAN = ['admis', 'doublon', 'refuse', 'manuel'];
/** Les deux résultats qui valent « la personne est entrée » (SCAN-08, POR-04). */
export const RESULTATS_SCAN_ENTREE = ['admis', 'manuel'];
/* -------------------------------------------------------------------------- */
/* Accès & sessions                                                           */
/* -------------------------------------------------------------------------- */
/** Rôles staff. `controle` accède uniquement aux soirées qui lui sont confiées. */
export const ROLE_UTILISATEUR = ['admin', 'guichet', 'lecture', 'controle', 'partenaire'];
/** Qui porte la session : un membre du personnel, ou un abonné du portail. */
export const SUJET_SESSION = ['staff', 'abonne'];
/* -------------------------------------------------------------------------- */
/* Emails                                                                     */
/* -------------------------------------------------------------------------- */
/** Nature de l'email envoyé, pour le journal `envoi_email` (DOC-03). */
export const TYPE_ENVOI_EMAIL = ['lien_magique', 'carte', 'billets', 'autre'];
/** Issue d'un envoi. Un échec est toujours visible sur la fiche, jamais silencieux. */
export const STATUT_ENVOI = ['envoye', 'echec'];
/* -------------------------------------------------------------------------- */
/* Imports                                                                    */
/* -------------------------------------------------------------------------- */
/** Un import se fait toujours en deux temps : simulation puis validation (IMP-01). */
export const STATUT_IMPORT_LOT = ['simule', 'valide'];
/** Les deux imports prévus : abonnés N-1 (IMP-01) et pièces vues (IMP-03). */
export const TYPE_IMPORT = ['abonnes', 'historique'];
/* -------------------------------------------------------------------------- */
/* Plan de salle (vue, pas stockage)                                          */
/* -------------------------------------------------------------------------- */
/**
 * État d'un siège pour une représentation donnée — calculé, jamais stocké
 * (ARCHITECTURE §8, §9.3). C'est le vocabulaire du composant `SeatMap`.
 *
 *  - `libre`       : vendable et sans billet valide ;
 *  - `vendu`       : billet d'origine `unite` ;
 *  - `abonnement`  : billet d'origine `abonnement` ;
 *  - `invitation`  : billet d'origine `invitation` (place donnée, montant 0) ;
 *  - `hors_vente`  : siège `vendable = false` (régie, PMR, défectueux — CFG-03) ;
 *  - `gele`        : porté par une proposition `en_attente` (CAM-04) ;
 *  - `reserve_abonnement` : promis par une souscription série encore vivante
 *    (brouillon ou confirmée) dont les billets ne sont pas encore émis — la
 *    place est déjà annoncée à l'abonné, elle n'est donc pas à vendre (SOU-01) ;
 *  - `retenu`      : **retenu par un autre** — un panier de la boutique tient
 *    la place le temps d'un paiement (`occupation_siege.nature = 'panier'`,
 *    non périmée). C'est un état TEMPORAIRE : il disparaît de lui-même à
 *    l'expiration, sans intervention humaine (REZ-04) ;
 *  - `indisponible` : ★ **l'état de la boutique publique**, et le SEUL que le
 *    public reçoive pour une place qu'il ne peut pas prendre. Il agrège
 *    `vendu`, `abonnement`, `invitation`, `gele`, `reserve_abonnement` et
 *    `retenu` en un unique mot. Ce n'est pas de la paresse d'affichage, c'est
 *    une exigence : REZ-06 veut les sièges gelés par une campagne
 *    **invisibles** à la boutique, et la LPD interdit d'annoncer au premier
 *    venu que la place F12 est occupée par un abonné (a fortiori de le nommer,
 *    ce que fait l'infobulle du back-office). Un visiteur n'a besoin que de
 *    savoir s'il peut s'asseoir ;
 *  - `selectionne` : état purement client, sélection en cours dans le SeatMap.
 */
export const ETAT_SIEGE = [
    'libre',
    'vendu',
    'abonnement',
    'invitation',
    'hors_vente',
    'gele',
    'reserve_abonnement',
    'retenu',
    'indisponible',
    'selectionne',
];
/** Les états renvoyés par le serveur : `selectionne` n'existe que côté client. */
export const ETAT_SIEGE_SERVEUR = [
    'libre',
    'vendu',
    'abonnement',
    'invitation',
    'hors_vente',
    'gele',
    'reserve_abonnement',
    'retenu',
    'indisponible',
];
/**
 * ★ Les trois seuls états que la **boutique publique** connaît (BOU-03).
 *
 * Le back-office distingue sept causes d'indisponibilité parce qu'il doit
 * pouvoir agir sur chacune. Le public n'en distingue que ce qui change sa
 * décision : je peux m'asseoir, je ne peux pas, ce fauteuil n'existe pas
 * commercialement. Restreindre le vocabulaire ici n'est pas cosmétique — c'est
 * ce qui rend REZ-06 (« les sièges gelés par une campagne restent invisibles »)
 * vrai par construction du type, et non par vigilance d'un développeur.
 */
export const ETAT_SIEGE_PUBLIC = [
    'libre',
    'indisponible',
    'hors_vente',
];
/* -------------------------------------------------------------------------- */
/* Libellés humains (NF-06 : catalogue de textes centralisé)                   */
/* -------------------------------------------------------------------------- */
export const LIBELLES_PHASE_SAISON = {
    preparation: 'Préparation',
    interne: 'Phase interne',
    externe: 'Phase externe',
    cloturee: 'Clôturée',
};
export const LIBELLES_STATUT_REPRESENTATION = {
    programmee: 'Programmée',
    annulee: 'Annulée',
};
export const LIBELLES_NATURE_FORMULE = {
    serie: 'Série imposée',
    choix: 'Spectacles au choix',
    carnet: 'Carnet',
};
export const LIBELLES_STATUT_SOUSCRIPTION = {
    brouillon: 'Brouillon',
    confirmee: 'Confirmée',
    annulee: 'Annulée',
};
export const LIBELLES_STATUT_PAIEMENT = {
    non_paye: 'Non payé',
    paye: 'Payé',
};
export const LIBELLES_ORIGINE_BILLET = {
    abonnement: 'Abonnement',
    unite: 'Vente à l’unité',
    invitation: 'Invitation',
};
export const LIBELLES_STATUT_BILLET = {
    valide: 'Valide',
    annule: 'Annulé',
};
export const LIBELLES_STATUT_CARTE = {
    active: 'Active',
    revoquee: 'Révoquée',
};
export const LIBELLES_STATUT_PROPOSITION = {
    en_attente: 'En attente',
    acceptee: 'Acceptée',
    declinee: 'Déclinée',
    expiree: 'Expirée',
};
export const LIBELLES_RESULTAT_SCAN = {
    admis: 'Admis',
    doublon: 'Déjà scanné',
    refuse: 'Refusé',
    manuel: 'Admission manuelle',
};
export const LIBELLES_ROLE_UTILISATEUR = {
    admin: 'Administrateur',
    guichet: 'Guichet',
    lecture: 'Lecture',
    controle: 'Contrôle d’entrée',
    partenaire: 'Partenaire',
};
export const LIBELLES_TYPE_ENVOI_EMAIL = {
    lien_magique: 'Lien de connexion',
    carte: 'Carte d’abonné',
    billets: 'Billets',
    autre: 'Autre',
};
export const LIBELLES_STATUT_ENVOI = {
    envoye: 'Envoyé',
    echec: 'Échec',
};
export const LIBELLES_ETAT_SIEGE = {
    libre: 'Libre',
    vendu: 'Vendu',
    abonnement: 'Abonnement',
    invitation: 'Invitation',
    hors_vente: 'Hors vente',
    gele: 'Réservé campagne',
    reserve_abonnement: 'Réservé abonnement',
    retenu: 'Retenu par un autre',
    indisponible: 'Indisponible',
    selectionne: 'Sélectionné',
};
export const LIBELLES_STATUT_PANIER = {
    actif: 'En cours',
    expire: 'Expiré',
    abandonne: 'Abandonné',
    valide: 'Commandé',
};
export const LIBELLES_GENRE_LIGNE_PANIER = {
    place: 'Place',
    abonnement: 'Abonnement',
};
/** BOU-11 — l'écran de guichet affiche « Vente téléphonique », jamais le jeton technique. */
export const LIBELLES_MODE_PANIER = {
    public: 'Boutique en ligne',
    guichet: 'Vente téléphonique',
};
export const LIBELLES_NATURE_OCCUPATION = {
    panier: 'Panier en cours',
    souscription: 'Abonnement (places promises)',
    billet: 'Billet émis',
    contingent: 'Contingent réservé',
};
export const LIBELLES_STATUT_IMPORT_LOT = {
    simule: 'Simulation',
    valide: 'Validé',
};
export const LIBELLES_STATUT_TRANSACTION = {
    initie: 'Initié',
    en_attente: 'En attente du paiement',
    confirme: 'Payé',
    echoue: 'Échoué',
    abandonne: 'Abandonné',
    rembourse: 'Remboursé',
};
export const LIBELLES_PRESTATAIRE_PAIEMENT = {
    simule: 'Paiement simulé (aucun encaissement réel)',
    stripe: 'Carte bancaire via Stripe',
    payrexx: 'Paiement en ligne via Payrexx',
};
export const LIBELLES_TYPE_REFERENCE_FACTURE = {
    qrr: 'Référence QR (QRR)',
    scor: 'Creditor Reference (SCOR)',
    sans: 'Sans référence',
};
export const LIBELLES_STATUT_FACTURE = {
    emise: 'Émise',
    annulee: 'Annulée',
};
/* -------------------------------------------------------------------------- */
/* Hiérarchie des rôles (SPEC §4 : admin > guichet > lecture)                  */
/* -------------------------------------------------------------------------- */
/**
 * Poids d'un rôle. Sert aux préhandlers `requireRole` et aux gardes de routes
 * du frontend. Un rôle couvre tous les rôles de poids inférieur UNIQUEMENT
 * lorsque l'écran est en lecture seule : `guichet` n'hérite PAS de `lecture`
 * pour les tableaux de bord et n'accède jamais à la configuration.
 * On compare donc explicitement, sans supposer d'héritage implicite.
 */
export const POIDS_ROLE = {
    admin: 30,
    guichet: 20,
    lecture: 10,
    controle: 5,
    partenaire: 1,
};
/** `true` si `role` fait partie des rôles attendus. */
export function aRole(role, ...attendus) {
    return role != null && attendus.includes(role);
}
/* -------------------------------------------------------------------------- */
/* Gardes de type                                                             */
/* -------------------------------------------------------------------------- */
/**
 * Fabrique une garde de type pour une enum du domaine.
 * `estPhaseSaison(x)` est plus lisible que `PHASE_SAISON.includes(x as never)`.
 */
function garde(valeurs) {
    const ensemble = new Set(valeurs);
    return (valeur) => typeof valeur === 'string' && ensemble.has(valeur);
}
export const estPhaseSaison = garde(PHASE_SAISON);
export const estModeSalle = garde(MODE_SALLE);
export const estStatutRepresentation = garde(STATUT_REPRESENTATION);
export const estPorteeTarif = garde(PORTEE_TARIF);
export const estNatureFormule = garde(NATURE_FORMULE);
export const estStatutSouscription = garde(STATUT_SOUSCRIPTION);
export const estStatutPaiement = garde(STATUT_PAIEMENT);
export const estOrigineBillet = garde(ORIGINE_BILLET);
export const estStatutBillet = garde(STATUT_BILLET);
export const estStatutCarte = garde(STATUT_CARTE);
export const estNatureOccupation = garde(NATURE_OCCUPATION);
export const estModePanier = garde(MODE_PANIER);
export const estStatutProposition = garde(STATUT_PROPOSITION);
export const estResultatScan = garde(RESULTAT_SCAN);
export const estRoleUtilisateur = garde(ROLE_UTILISATEUR);
export const estSujetSession = garde(SUJET_SESSION);
export const estTypeEnvoiEmail = garde(TYPE_ENVOI_EMAIL);
export const estStatutEnvoi = garde(STATUT_ENVOI);
export const estStatutImportLot = garde(STATUT_IMPORT_LOT);
export const estStatutTransaction = garde(STATUT_TRANSACTION);
export const estPrestatairePaiement = garde(PRESTATAIRE_PAIEMENT);
export const estTypeReferenceFacture = garde(TYPE_REFERENCE_FACTURE);
export const estStatutFacture = garde(STATUT_FACTURE);
export const estVariableRelance = garde(VARIABLES_RELANCE);
export const estTypeImport = garde(TYPE_IMPORT);
export const estEtatSiege = garde(ETAT_SIEGE);
//# sourceMappingURL=enums.js.map